import React from "react";
import { Mountain, Truck, FileText, Download, Package, Factory, Globe, ShieldCheck, MessageCircle, Instagram, MapPin, Users, Phone } from "lucide-react";

const IMG_CEMENT = "https://static.prod-images.emergentagent.com/jobs/44e922b4-6326-4e9e-a61b-a036e60b36dc/images/ae77fbed94c451c4bb3872c7e764f6a1a7fe2bd85cd0bdd9365dad791ab2568c.png";

export default function CementPage({ onDownload, documents }) {
  const ncndaDoc = documents?.find((d) => d.id === "doc-ncnda");

  return (
    <div data-testid="view-cement" className="max-w-5xl">
      <Hero />

      <Section eyebrow="Fondamentaux" title="Le ciment : la colonne vertébrale de la construction mondiale" index="01">
        <p>
          Avec <strong className="text-white">4,1 milliards de tonnes produites chaque année</strong>, le ciment est le
          second produit le plus consommé au monde après l'eau. Chaque immeuble, chaque pont, chaque route en contient.
          C'est un marché colossal, très fragmenté — donc très ouvert à l'intermédiation.
        </p>
        <ul className="mt-6 space-y-3 text-[#A1A1AA]">
          <BulletPoint title="Volumes massifs, marges fines">Les deals se font au cargo (25–50 000 t) ou au conteneur (25 t). Marges 2–5 % mais volumes énormes.</BulletPoint>
          <BulletPoint title="Demande structurelle">Urbanisation Afrique / Asie du Sud-Est = croissance à 2 chiffres sur 10 ans.</BulletPoint>
          <BulletPoint title="Logistique = l'arène">La vraie bataille se joue sur le fret maritime, le port de destination et la rotation des camions.</BulletPoint>
          <BulletPoint title="Peu d'arnaques élaborées">Contrairement à l'or ou au pétrole, le ciment se vérifie vite à réception (test labo). Marché plus "clean".</BulletPoint>
        </ul>
      </Section>

      <Section eyebrow="Produit" title="Clinker, ciment, additifs : comprendre la matière" index="02">
        <p>
          Le ciment Portland est obtenu en broyant le <strong className="text-white">clinker</strong> (roche calcinée à
          1 450 °C) avec ~5 % de gypse. Le clinker est <strong className="text-white">l'intrant stratégique</strong>
          souvent exporté seul vers des cimenteries de finition.
        </p>
        <div className="mt-10 border border-white/10">
          {[
            { step: "01", title: "Calcaire + argile", detail: "Carrières de proximité. Ratio 80/20 environ. Broyage fin, homogénéisation.", pv: "Coût matière première" },
            { step: "02", title: "Four rotatif à 1 450 °C", detail: "Décarbonatation + clinkerisation. Étape la plus énergivore (40 % du coût).", pv: "CO₂ : 0,9 t par tonne clinker" },
            { step: "03", title: "Clinker nodulaire", detail: "Produit intermédiaire stockable et transportable en vrac maritime.", pv: "≈ 40–55 USD/t FOB" },
            { step: "04", title: "Broyage + gypse + ajouts", detail: "Clinker + gypse + laitier/cendre/pouzzolane → ciment fini.", pv: "Valeur ajoutée cimenterie" },
            { step: "05", title: "Ensachage ou vrac", detail: "Big bag 1–1,5 t, sacs 25/50 kg, ou vrac camion-citerne.", pv: "≈ 55–95 USD/t FOB" },
            { step: "06", title: "Livraison chantier", detail: "Camion toupie (béton prêt à l'emploi) ou ensaché.", pv: "Retail : 120–180 USD/t" },
          ].map((r) => (
            <div key={r.step} className="grid grid-cols-12 border-b border-white/10 last:border-b-0 p-5 md:p-6 hover:bg-[#141414] transition-colors">
              <div className="col-span-2 md:col-span-1 font-serif text-xl text-[#D4AF37]">{r.step}</div>
              <div className="col-span-10 md:col-span-7">
                <div className="font-serif text-lg">{r.title}</div>
                <div className="text-sm text-[#A1A1AA] mt-2">{r.detail}</div>
              </div>
              <div className="col-span-12 md:col-span-4 md:text-right mt-3 md:mt-0">
                <span className="text-[10px] uppercase tracking-[0.28em] text-[#D4AF37]">Valeur</span>
                <div className="font-mono text-white mt-1 text-sm">{r.pv}</div>
              </div>
            </div>
          ))}
        </div>
        <Callout icon={Factory} title="Où se positionne l'intermédiaire">
          Deux créneaux gagnants : <strong className="text-white">(a)</strong> l'export de <em>clinker</em> depuis
          pays excédentaires (Turquie, Vietnam, UAE) vers cimenteries de finition d'Afrique de l'Ouest ;
          <strong className="text-white"> (b)</strong> l'import de ciment fini ensaché pour gros projets
          (BTP, infrastructures, ports) où l'acheteur local n'a ni la capacité financière ni la structure pour dealer en direct.
        </Callout>
      </Section>

      <Section eyebrow="Types" title="CEM I, II, III, IV, V et au-delà" index="03">
        <p>
          La norme européenne <strong className="text-white">EN 197-1</strong> classe les ciments en 5 familles selon
          le pourcentage de clinker. Les acheteurs internationaux raisonnent aussi en grades
          <strong className="text-white"> 32,5 / 42,5 / 52,5</strong> (résistance à 28 jours en MPa).
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-10">
          {[
            { code: "CEM I", name: "Ciment Portland", desc: "95–100 % clinker. Le plus courant. Haute résistance initiale.", use: "BTP général, béton précontraint, travaux à prise rapide." },
            { code: "CEM II", name: "Composite au laitier/cendre", desc: "65–94 % clinker + ajouts (laitier, cendres volantes, pouzzolane, calcaire).", use: "Usage général, meilleur bilan carbone. Dominant en Europe." },
            { code: "CEM III", name: "Haut fourneau (laitier)", desc: "35–64 % clinker + 36–65 % laitier. Résistance aux sulfates.", use: "Ouvrages maritimes, fondations en milieu agressif, barrages." },
            { code: "CEM IV", name: "Pouzzolanique", desc: "45–89 % clinker + pouzzolane naturelle/cendres. Prise lente, durable.", use: "Grands ouvrages massifs, zones chaudes, tropicales." },
            { code: "CEM V", name: "Composite (laitier + pouzzolane)", desc: "20–64 % clinker + mix laitier/pouzzolane.", use: "Béton très durable. Usages spécifiques." },
            { code: "OPC 42.5 / 52.5", name: "Ordinary Portland (CEM I équiv.)", desc: "Standard export international. Prix repère du marché.", use: "Usage général, routes, logement collectif." },
            { code: "SRC", name: "Sulfate Resistant Cement", desc: "Ciment résistant aux sulfates. C3A limité à 5 %.", use: "Ouvrages maritimes, stations d'épuration." },
            { code: "White Cement", name: "Ciment blanc", desc: "Spécialité esthétique. Prix 2–3× ciment gris.", use: "Façades, architecture, préfabrication décorative." },
          ].map((c) => (
            <div key={c.code} className="border border-white/10 bg-[#0c0c0c] p-6 hover:border-[#D4AF37]/30 transition-colors">
              <div className="flex items-baseline justify-between">
                <span className="font-mono text-[#D4AF37]">{c.code}</span>
              </div>
              <div className="font-serif text-lg mt-2">{c.name}</div>
              <p className="text-sm text-[#A1A1AA] mt-2">{c.desc}</p>
              <div className="mt-4 pt-4 border-t border-white/10 text-xs text-[#C4C4C7]">{c.use}</div>
            </div>
          ))}
        </div>
      </Section>

      <Section eyebrow="Conditionnement & logistique" title="Big bag, sac 50 kg, vrac : choisir le bon format" index="04">
        <p>
          Le format détermine le prix à la tonne, les coûts de manutention, les pertes et la rapidité de livraison.
          Le choix dépend du port de destination et du chantier final.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-8">
          {[
            { title: "Vrac maritime", icon: "🚢", desc: "25 000–50 000 t par cargo. Navires bulk carrier ou cimentier spécialisé. Rotation ~15 jours Turquie-Afrique de l'Ouest.", pro: "Prix /t le plus bas", con: "Nécessite silos au port de destination" },
            { title: "Big bag (1–1,5 t)", icon: "📦", desc: "Containers 20'/40'. 20 big bags par 40'HC. Solution polyvalente, sans silo nécessaire à l'arrivée.", pro: "Flexibilité", con: "Prix /t +8 à +15 %" },
            { title: "Sac 25/50 kg", icon: "🧱", desc: "Containers palettisés. Livré directement au client final ou distributeur. Standard retail africain.", pro: "Directement vendable", con: "Prix /t +20 à +35 %" },
          ].map((f) => (
            <div key={f.title} className="border border-white/10 bg-[#0c0c0c] p-6">
              <div className="text-3xl">{f.icon}</div>
              <div className="font-serif text-lg mt-3 text-white">{f.title}</div>
              <p className="text-sm text-[#A1A1AA] mt-3">{f.desc}</p>
              <div className="mt-4 pt-4 border-t border-white/10 space-y-2 text-xs">
                <div className="text-[#34C759]">+ {f.pro}</div>
                <div className="text-[#A1A1AA]">− {f.con}</div>
              </div>
            </div>
          ))}
        </div>
      </Section>

      <Section eyebrow="Flux commerciaux" title="Les grandes routes du ciment mondial" index="05">
        <p>
          Le ciment est un produit <strong className="text-white">lourd mais peu cher</strong> : le fret pèse 20–40 %
          du prix livré. Les flux internationaux suivent logiquement les écarts entre pays à surcapacité (exportateurs)
          et pays en construction (importateurs).
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-10">
          {[
            { origin: "🇹🇷 Turquie", dest: "Afrique de l'Ouest, Afrique de l'Est, Amérique du Sud", vol: "25 Mt/an", notes: "Leader export mondial. Cimenteries modernes, prix 45–55 USD/t FOB." },
            { origin: "🇻🇳 Vietnam", dest: "Philippines, Bangladesh, Afrique de l'Est", vol: "30 Mt/an", notes: "Énorme surcapacité. Prix très agressifs 38–48 USD/t FOB." },
            { origin: "🇦🇪 UAE (Fujairah, Jebel Ali)", dest: "Afrique de l'Est, Yémen, Océan Indien", vol: "8 Mt/an", notes: "Hub de réexportation. Ciment blanc premium." },
            { origin: "🇪🇬 Égypte", dest: "Soudan, Libye, Afrique de l'Ouest", vol: "5 Mt/an", notes: "Surcapacité domestique. Prix compétitifs CIF Afrique." },
            { origin: "🇨🇳 Chine", dest: "Asie du Sud-Est, Afrique", vol: "15 Mt/an", notes: "Excédents massifs. Qualité variable — vérifier spécifications." },
            { origin: "🇳🇬 Nigeria (Dangote)", dest: "Afrique de l'Ouest (Togo, Bénin, Ghana, Côte d'Ivoire)", vol: "12 Mt/an", notes: "Leader régional. Capacité de production interne 45 Mt." },
          ].map((f) => (
            <div key={f.origin} className="border border-white/10 bg-[#0c0c0c] p-6">
              <div className="flex items-baseline justify-between">
                <span className="font-serif text-lg">{f.origin}</span>
                <span className="text-[10px] uppercase tracking-[0.28em] text-[#D4AF37]">{f.vol}</span>
              </div>
              <div className="text-xs text-[#D4AF37] mt-3 uppercase tracking-[0.2em]">→ Vers</div>
              <div className="text-sm text-white mt-1">{f.dest}</div>
              <p className="mt-4 pt-4 border-t border-white/10 text-xs text-[#A1A1AA]">{f.notes}</p>
            </div>
          ))}
        </div>
      </Section>

      <Section eyebrow="Procédure" title="Cadrer un deal ciment de A à Z" index="06">
        <p>
          La séquence diffère peu du pétrole, mais les <strong className="text-white">vérifications terrain</strong>
          (tests labo, SGS au chargement, SGS au déchargement) sont plus légères — le ciment se révèle vite à l'usage.
        </p>
        <div className="mt-8 space-y-3">
          {[
            { step: "01", title: "NCNDA + IMFPA", detail: "Cadrer la chaîne de brokers. Tous les intermédiaires signent. Commissions 1–3 %." },
            { step: "02", title: "ICPO acheteur", detail: "Irrevocable Corporate Purchase Order. Spéc produit, port, quantité, mode de paiement." },
            { step: "03", title: "FCO vendeur", detail: "Prix, Incoterm (FOB/CIF/CFR), délais de chargement, inspection SGS, pénalités." },
            { step: "04", title: "Product Spec Sheet + Certificate of Quality", detail: "Fiche technique cimenterie + rapport labo (résistance, finesse Blaine, C3A, SO₃)." },
            { step: "05", title: "SPA signé + MT799 readiness", detail: "Sales & Purchase Agreement ferme. Acheteur confirme capacité financière via SWIFT MT799." },
            { step: "06", title: "MT760 SBLC ou DLC à vue", detail: "Garantie bancaire opérationnelle. 2 % frais. Conditions documentaires UCP 600." },
            { step: "07", title: "Chargement + BL + SGS Quality & Quantity", detail: "Bill of Lading, Certificate of Origin, SGS au port de chargement." },
            { step: "08", title: "Déchargement + dip test optionnel", detail: "Port de destination. Certificat de conformité final → paiement MT103 + commission IMFPA." },
          ].map((s) => (
            <div key={s.step} className="flex gap-6 border border-white/10 bg-[#0c0c0c] p-5 hover:border-white/25 transition-colors">
              <div className="font-serif text-xl text-[#D4AF37] shrink-0">{s.step}</div>
              <div>
                <div className="font-serif text-lg text-white">{s.title}</div>
                <div className="text-sm text-[#A1A1AA] mt-2">{s.detail}</div>
              </div>
            </div>
          ))}
        </div>
      </Section>

      <Section eyebrow="Ressources" title="Documents pour un deal ciment" index="07">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <DocRef doc={ncndaDoc} onDownload={onDownload} accent="NCNDA — Chaîne brokers" note="Indispensable avant tout échange. Les mêmes mécaniques que pour l'or et le pétrole." />
        </div>
        <p className="mt-6 text-xs text-[#52525B]">
          Documents sectoriels complémentaires à joindre : Company Profile cimenterie, Product Spec Sheet
          (résistance Rc 2j/28j, finesse Blaine, chimie complète), Certificate of Analysis (CoA), Mill Test Certificate,
          Certificate of Origin, SGS Quality & Quantity Inspection Report.
        </p>
      </Section>

      <Section eyebrow="Rolodex" title="Fournisseurs — Ciment, sucre & commodités" index="08">
        <p>
          Les contacts directs pour vos deals en <strong className="text-white">ciment, sucre, riz et autres commodités</strong>.
          WhatsApp cliquable pour contact instantané.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-8">
          <SupplierCard
            name="MC International"
            tag="Multi-commodités — sucre, ciment, riz"
            location="Thailand"
            whatsapps={["+66 99 437 2193"]}
            website="https://mcspaco.com"
          />
          <SupplierCard
            name="Exelaa Commodities"
            tag="Commodités & trading"
            location="India"
            whatsapps={["+91 94816 74355"]}
            website="https://www.exelaa.co.in"
          />
          <SupplierCard
            name="Tejah International"
            tag="Trading international"
            location="India"
            whatsapps={["+91 94480 82323"]}
            website="https://www.tejahinternational.site"
          />
          <SupplierCard
            name="Novalux Trading LLC"
            tag="Trading — Dubaï"
            location="Dubai, UAE"
            whatsapps={["+971 50 501 7357"]}
          />
          <SupplierCard
            name="National Cement"
            tag="Cimenterie intégrée"
            location="Dubai, UAE"
            phone="+971 4 338 8885"
            website="https://nationalcement.ae/products-and-services/"
          />
        </div>
      </Section>
    </div>
  );
}

// ---------- Subcomponents ----------
const Hero = () => (
  <section className="relative mb-16 border border-white/10 bg-[#0c0c0c] overflow-hidden">
    <img src={IMG_CEMENT} alt="" className="absolute inset-0 w-full h-full object-cover opacity-40" />
    <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0A] via-[#0A0A0A]/85 to-transparent" />
    <div className="relative p-10 md:p-14">
      <div className="flex items-center gap-3">
        <Mountain className="h-5 w-5 text-[#D4AF37]" />
        <span className="text-[11px] uppercase tracking-[0.4em] text-[#D4AF37]">Secteur · 04</span>
      </div>
      <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl font-light leading-tight mt-6 max-w-3xl">
        Le <em className="italic text-[#F5F5DC]">Ciment</em> — gros volumes, <span className="text-[#D4AF37]">logistique reine</span>.
      </h1>
      <p className="mt-6 max-w-2xl text-[#A1A1AA] leading-relaxed">
        Clinker, CEM I à V, big bags et vrac, flux Turquie/Vietnam/UAE vers l'Afrique : le marché le plus propre
        du portefeuille, celui où la marge vient du fret et de la fiabilité.
      </p>
    </div>
  </section>
);

const Section = ({ eyebrow, title, index, children }) => (
  <section className="mb-20">
    <div className="flex items-baseline gap-6 mb-8">
      <span className="font-serif text-xl text-[#D4AF37] tabular-nums">{index}</span>
      <div>
        <div className="text-[10px] uppercase tracking-[0.32em] text-[#D4AF37]">{eyebrow}</div>
        <h2 className="font-serif text-2xl md:text-3xl lg:text-4xl font-light mt-2 leading-tight">{title}</h2>
      </div>
    </div>
    <div className="text-[#C4C4C7] leading-relaxed space-y-4 pl-0 md:pl-10">{children}</div>
  </section>
);

const BulletPoint = ({ title, children }) => (
  <li className="flex gap-3">
    <span className="mt-2 h-1 w-1 bg-[#D4AF37] shrink-0" />
    <div>
      <span className="text-white">{title}.</span> <span>{children}</span>
    </div>
  </li>
);

const Callout = ({ icon: Icon, title, children }) => (
  <div className="mt-10 border-l-2 border-[#D4AF37] bg-[#141414] p-6 md:p-8">
    <div className="flex items-center gap-3">
      <Icon className="h-4 w-4 text-[#D4AF37]" />
      <span className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37]">{title}</span>
    </div>
    <div className="mt-4 text-[#E4E4E7] leading-relaxed text-sm">{children}</div>
  </div>
);

const DocRef = ({ doc, onDownload, accent, note }) => {
  if (!doc) return null;
  return (
    <div className="border border-white/10 bg-[#0c0c0c] p-6 group hover:border-[#D4AF37]/30 transition-colors">
      <div className="flex items-center gap-3">
        <FileText className="h-4 w-4 text-[#D4AF37]" />
        <span className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37]">{accent}</span>
      </div>
      <div className="font-serif text-xl mt-3">{doc.name}</div>
      <p className="text-sm text-[#A1A1AA] mt-3">{note}</p>
      <button
        onClick={() => onDownload(doc)}
        data-testid={`cement-download-${doc.id}`}
        className="mt-5 inline-flex items-center gap-2 px-5 py-3 border border-white/15 text-xs uppercase tracking-[0.22em] hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors"
      >
        <Download className="h-3.5 w-3.5" /> Télécharger PDF
      </button>
    </div>
  );
};

// Reusable supplier/contact card
const SupplierCard = ({ name, tag, location, whatsapps = [], website, instagram, phone }) => (
  <div data-testid={`supplier-card-${name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} className="border border-white/10 bg-[#0c0c0c] p-6 md:p-7 hover:border-[#D4AF37]/50 transition-colors flex flex-col">
    <div className="flex items-center gap-3">
      <Users className="h-4 w-4 text-[#D4AF37]" />
      <span className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37]">{tag}</span>
    </div>
    <h3 className="font-serif text-xl mt-3 text-white">{name}</h3>
    {location && (
      <div className="mt-2 flex items-center gap-2 text-xs text-[#A1A1AA]">
        <MapPin className="h-3.5 w-3.5 text-[#D4AF37]" />
        <span>{location}</span>
      </div>
    )}
    <div className="mt-5 space-y-2.5 text-sm">
      {whatsapps.map((w) => (
        <a
          key={w}
          href={`https://wa.me/${w.replace(/\D/g, "")}`}
          target="_blank"
          rel="noreferrer"
          className="flex items-center gap-3 text-[#A1A1AA] hover:text-[#25D366] transition-colors group"
        >
          <MessageCircle className="h-4 w-4 shrink-0 text-[#25D366] group-hover:scale-110 transition-transform" />
          <span className="font-mono tracking-tight">{w}</span>
        </a>
      ))}
      {phone && (
        <a
          href={`tel:${phone.replace(/\s/g, "")}`}
          className="flex items-center gap-3 text-[#A1A1AA] hover:text-[#D4AF37] transition-colors"
        >
          <Phone className="h-4 w-4 shrink-0 text-[#D4AF37]" />
          <span className="font-mono tracking-tight">{phone}</span>
        </a>
      )}
      {website && (
        <a
          href={website}
          target="_blank"
          rel="noreferrer"
          className="flex items-center gap-3 text-[#A1A1AA] hover:text-[#D4AF37] transition-colors break-all"
        >
          <Globe className="h-4 w-4 shrink-0 text-[#D4AF37]" />
          <span className="truncate">{website.replace(/^https?:\/\//, "")}</span>
        </a>
      )}
      {instagram && (
        <a
          href={`https://instagram.com/${instagram.replace(/^@/, "")}`}
          target="_blank"
          rel="noreferrer"
          className="flex items-center gap-3 text-[#A1A1AA] hover:text-[#E1306C] transition-colors"
        >
          <Instagram className="h-4 w-4 shrink-0 text-[#E1306C]" />
          <span>@{instagram.replace(/^@/, "")}</span>
        </a>
      )}
    </div>
  </div>
);

